
import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Star, TrendingUp, Sparkles, Eye, EyeOff, Wand2, Loader2 } from 'lucide-react';
import { useAIContentGeneration } from '@/hooks/useAIContentGeneration';

interface GameEditDialogProps {
  game: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface GameFormData {
  title: string;
  description: string;
  category: string;
  thumbnail: string;
  tags: string;
  featured: boolean;
  trending: boolean;
  is_new: boolean;
  hidden: boolean;
  rating: number;
  fake_play_count: number;
  game_url: string;
}

const GameEditDialog: React.FC<GameEditDialogProps> = ({ game, open, onOpenChange }) => {
  const queryClient = useQueryClient();
  const { generateDescription, generateTags, isLoading: isAILoading } = useAIContentGeneration();

  // Fetch categories for the dropdown
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  const form = useForm<GameFormData>({
    defaultValues: {
      title: '',
      description: '',
      category: '',
      thumbnail: '',
      game_url: '',
      tags: '',
      featured: false,
      trending: false,
      is_new: false,
      hidden: false,
      rating: 0,
      fake_play_count: 0,
    },
  });

  // Reset form when game changes
  useEffect(() => {
    if (game) {
      form.reset({
        title: game.title || '',
        description: game.description || '',
        category: game.category || '',
        thumbnail: game.thumbnail || '',
        game_url: game.game_url || '',
        tags: game.tags ? game.tags.join(', ') : '',
        featured: game.featured || false,
        trending: game.trending || false,
        is_new: game.is_new || false,
        hidden: game.hidden || false,
        rating: game.rating || 0,
        fake_play_count: game.fake_play_count || game.play_count || 0,
      });
    } else {
      // Reset for new game
      form.reset({
        title: '',
        description: '',
        category: '',
        thumbnail: '',
        game_url: '',
        tags: '',
        featured: false,
        trending: false,
        is_new: true,
        hidden: false,
        rating: 0,
        fake_play_count: 0,
      });
    }
  }, [game, form]);

  // Create/Update game mutation
  const saveGameMutation = useMutation({
    mutationFn: async (data: GameFormData) => {
      const gameData = {
        ...data,
        tags: data.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        updated_at: new Date().toISOString(),
        embed_type: 'iframe',
        game_file: null,
      };

      if (game) {
        // Update existing game
        const { error } = await supabase
          .from('games')
          .update(gameData)
          .eq('id', game.id);
        
        if (error) throw error;
      } else {
        // Create new game
        const { error } = await supabase
          .from('games')
          .insert([{
            id: crypto.randomUUID(),
            ...gameData,
            created_at: new Date().toISOString(),
          }]);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-games'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      onOpenChange(false);
      toast.success(game ? 'Game updated successfully' : 'Game created successfully');
    },
    onError: (error) => {
      console.error('Save game error:', error);
      toast.error(`Failed to ${game ? 'update' : 'create'} game: ` + error.message);
    },
  });

  const onSubmit = (data: GameFormData) => {
    console.log('Submitting game data:', data);
    saveGameMutation.mutate(data);
  };

  const handleGenerateDescription = async () => {
    const currentFormData = form.getValues();
    if (!currentFormData.title) {
      toast.error('Please enter a game title first');
      return;
    }

    try {
      toast.info('Generating description with AI...');
      const generatedDescription = await generateDescription({
        title: currentFormData.title,
        category: currentFormData.category,
        description: currentFormData.description,
      });
      
      form.setValue('description', generatedDescription);
      toast.success('Description generated successfully!');
    } catch (error) {
      console.error('Failed to generate description:', error);
    }
  };

  const handleGenerateTags = async () => {
    const currentFormData = form.getValues();
    if (!currentFormData.title) {
      toast.error('Please enter a game title first');
      return;
    }

    try {
      toast.info('Generating tags with AI...');
      const generatedTags = await generateTags({
        title: currentFormData.title,
        category: currentFormData.category,
        description: currentFormData.description,
      });
      
      form.setValue('tags', generatedTags);
      toast.success('Tags generated successfully!');
    } catch (error) {
      console.error('Failed to generate tags:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            {game ? `Edit Game: ${game.title}` : 'Add New Game'}
            <Badge variant="secondary" className="text-xs">
              ✨ AI Powered
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title *</FormLabel>
                      <FormControl>
                        <Input placeholder="Game title" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.slug}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center justify-between">
                      Description
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleGenerateDescription}
                        disabled={isAILoading}
                        className="h-8 px-3 text-xs"
                      >
                        {isAILoading ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Wand2 className="w-3 h-3 mr-1" />
                        )}
                        AI Generate
                      </Button>
                    </FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Game description (or use AI to generate)" 
                        rows={3}
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="thumbnail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Thumbnail URL</FormLabel>
                      <FormControl>
                        <Input placeholder="https://..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="game_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Game URL *</FormLabel>
                      <FormControl>
                        <Input placeholder="https://yourdomain.com/games/game-name" {...field} value={field.value || ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="tags"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center justify-between">
                        Tags
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleGenerateTags}
                          disabled={isAILoading}
                          className="h-6 px-2 text-xs"
                        >
                          {isAILoading ? (
                            <Loader2 className="w-2 h-2 mr-1 animate-spin" />
                          ) : (
                            <Wand2 className="w-2 h-2 mr-1" />
                          )}
                          AI
                        </Button>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="action, adventure, puzzle" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="rating"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rating (0-5)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          min="0" 
                          max="5" 
                          step="0.1"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="fake_play_count"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Display Play Count</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          min="0"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Status and Visibility */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                Status & Visibility
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="hidden"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="space-y-1">
                          <FormLabel className="flex items-center gap-2">
                            {field.value ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            Visibility
                          </FormLabel>
                          <p className="text-sm text-gray-600">
                            {field.value ? 'Game is hidden from users' : 'Game is visible to users'}
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={!field.value}
                            onCheckedChange={(checked) => field.onChange(!checked)}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="featured"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="space-y-1">
                          <FormLabel className="flex items-center gap-2">
                            <Star className="w-4 h-4 text-yellow-500" />
                            Featured
                            {field.value && <Badge variant="secondary">Featured</Badge>}
                          </FormLabel>
                          <p className="text-sm text-gray-600">
                            Show in featured games section
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="trending"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="space-y-1">
                          <FormLabel className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-green-500" />
                            Trending
                            {field.value && <Badge variant="secondary">Trending</Badge>}
                          </FormLabel>
                          <p className="text-sm text-gray-600">
                            Show in trending games section
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="is_new"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="space-y-1">
                          <FormLabel className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-blue-500" />
                            New Game
                            {field.value && <Badge variant="secondary">New</Badge>}
                          </FormLabel>
                          <p className="text-sm text-gray-600">
                            Mark as a new game
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saveGameMutation.isPending}
                className="min-w-[100px]"
              >
                {saveGameMutation.isPending ? 'Saving...' : (game ? 'Save Changes' : 'Create Game')}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default GameEditDialog;
